import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { BillingAccount, Pack, PacksResponse } from "../api";
import { isEsnRole, useAuthenticatedSession } from "../session";
import { useWalletScope } from "../wallet";
import {
  api,
  useApiMutation,
  useApiQuery,
  useIdempotencyKey,
} from "../api";
import {
  Alert,
  Button,
  Card,
  EmptyState,
  SectionTitle,
  Skeleton,
  cx,
  useToast,
} from "../components";
import { formatEuros, formatTokens } from "../format";
import { useCardEntrance, useShake } from "../motion";
import { AsyncBoundary, PageHeader, ROUTES } from "../shell";
import { situerGamme } from "./AchatGamme";
import { AchatPackCarte } from "./AchatPackCarte";
import { AchatRecu } from "./AchatRecu";
import { AchatRefus } from "./AchatRefus";
import styles from "./AchatPage.module.css";

/** Pictogramme de panier, pour les états vides. Décoratif. */
function IconePanier() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="28"
      height="28"
      aria-hidden="true"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.6}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 6h2l2.2 9.4a1.5 1.5 0 0 0 1.5 1.1h7.1a1.5 1.5 0 0 0 1.5-1.2L20 8H6.5" />
      <circle cx="10" cy="20" r="1.2" />
      <circle cx="17.5" cy="20" r="1.2" />
    </svg>
  );
}

/**
 * Écran « achat de packs ».
 *
 * C'est le seul écran du produit qui déclenche un paiement, et tout y tourne
 * autour d'une seule question : comment garantir qu'un pack payé une fois n'est
 * crédité qu'une fois ? Réponse en trois couches, de la plus grossière à la plus
 * sûre :
 *
 * 1. le paiement demande deux gestes — désigner un pack, puis le confirmer ;
 * 2. le bouton de confirmation se désactive pendant l'envoi (`loading`) ;
 * 3. **la clé d'idempotence**, seule protection qui tienne encore quand la
 *    réponse se perd en route. Son cycle de vie est détaillé plus bas.
 */
export function AchatPage() {
  const { walletId, companyId } = useWalletScope();
  const { user } = useAuthenticatedSession();
  const esn = isEsnRole(user.role);
  const { notify, notifyError } = useToast();
  // L'entreprise n'achète qu'en se faisant prélever : son moyen de paiement
  // décide si elle peut confirmer. L'ESN, elle, enregistre un achat réglé
  // hors plateforme et n'a rien à lire.
  const compte = useApiQuery<BillingAccount | null>(
    (o) => (esn ? Promise.resolve(null) : api.getCompanyBilling(companyId, o)),
    [esn, companyId],
  );
  const blocage = esn ? null : raisonBlocage(compte.data);

  const catalogue = useApiQuery<PacksResponse>((o) => api.listPacks(o), []);

  // Situer la gamme est une comparaison de tous les packs entre eux : la faire
  // à l'intérieur de la boucle de rendu la referait une fois par carte.
  const gamme = useMemo(
    () => situerGamme(catalogue.data?.packs ?? []),
    [catalogue.data],
  );
  const achat = useApiMutation(api.creditPack);
  const {
    data: recu,
    error: erreurAchat,
    pending: envoiEnCours,
    run: lancerAchat,
    reset: reinitialiserAchat,
  } = achat;

  const [packChoisi, setPackChoisi] = useState<Pack | null>(null);

  /*
   * ------------------------------------------------------------------------
   * Cycle de vie de la clé d'idempotence
   * ------------------------------------------------------------------------
   * La clé identifie **l'intention d'achat**, pas la tentative d'envoi. Elle
   * est donc fabriquée une fois par intention et réutilisée telle quelle à
   * chaque nouvel essai.
   *
   * Elle change dans exactement deux cas :
   *
   * - `deps = [packChoisi?.id]` : désigner un **autre** pack, c'est vouloir
   *   acheter autre chose — une autre intention, donc une autre clé ;
   * - `renew()` explicite **après un succès seulement**, quand l'utilisateur
   *   redemande le même pack. Sans ce renouvellement, le second envoi porterait
   *   la clé du premier : le serveur y verrait un rejeu, rendrait le reçu déjà
   *   émis et ne créditerait rien — l'utilisateur croirait avoir payé deux fois
   *   pour un seul crédit.
   *
   * Et surtout, elle ne change **pas** après un échec : un refus métier, une
   * coupure réseau ou un 500 laissent planer le doute sur ce qui est arrivé au
   * serveur. Rejouer la même clé est précisément ce qui rend ce doute inoffensif
   * — si l'achat était passé, le serveur rend son reçu au lieu de créditer une
   * seconde fois. Une clé neuve par tentative, elle, désactiverait l'idempotence
   * au moment exact où on en a besoin.
   *
   * Il n'y a volontairement pas de bouton « annuler » qui remettrait
   * `packChoisi` à `null` : ce retour ferait tourner la clé sur une intention
   * peut-être déjà partie. On change d'avis en désignant un autre pack.
   */
  const { key: cleIdempotence, renew: renouvelerCle } = useIdempotencyKey([
    packChoisi?.id ?? null,
  ]);

  const { ref: refSecousse, shake } = useShake<HTMLDivElement>();

  const choisirPack = useCallback(
    (pack: Pack) => {
      // Un achat abouti clôt son intention. En redemander un — même pack ou
      // non — est un fait nouveau, qui mérite une clé neuve. Un achat échoué,
      // lui, garde la sienne : voir le commentaire ci-dessus.
      if (recu !== null) {
        reinitialiserAchat();
        renouvelerCle();
      }
      setPackChoisi(pack);
    },
    [recu, reinitialiserAchat, renouvelerCle],
  );

  const confirmerAchat = useCallback(async () => {
    if (packChoisi === null) {
      return;
    }
    // `run` ne lève jamais : l'erreur atterrit dans `achat.error`, lue par
    // l'effet ci-dessous.
    await lancerAchat(
      walletId,
      { pack_id: packChoisi.id },
      { idempotencyKey: cleIdempotence },
    );
  }, [packChoisi, lancerAchat, cleIdempotence, walletId]);

  // Le retour de `run` ne dit que « réussi ou non » ; l'erreur, elle, arrive par
  // l'état. La lire juste après l'`await` rendrait la valeur du rendu précédent
  // (fermeture périmée), donc toujours `null` au premier refus. On réagit au
  // changement d'état, ce qui est exact quel que soit le moment où il survient.
  useEffect(() => {
    if (erreurAchat === null) {
      return;
    }
    notifyError(erreurAchat, "Achat refusé");
    // La secousse dit « non » avant même que le texte soit lu, et pointe la
    // carte fautive au lieu d'un bandeau en haut de page.
    shake();
  }, [erreurAchat, notifyError, shake]);

  useEffect(() => {
    if (recu === null) {
      return;
    }
    notify({
      title: "Pack crédité",
      description: `${formatTokens(recu.entry.amount)} tokens ajoutés au disponible.`,
      tone: "success",
    });
  }, [recu, notify]);

  // Trois états du panneau de droite, et un seul à la fois.
  const etape = recu !== null ? "recu" : packChoisi !== null ? "confirmation" : "invite";
  const clePanneau = `${etape}:${packChoisi?.id ?? ""}`;

  // Entrée des cartes : la grille du catalogue, et le panneau qui se substitue.
  const grille = useCardEntrance<HTMLUListElement>({
    dependencies: [catalogue.data],
  });
  // `HTMLElement` et non `HTMLDivElement` : la ref se pose sur un `<aside>`.
  const entreePanneau = useCardEntrance<HTMLElement>({
    dependencies: [clePanneau],
  });

  // Le contenu du panneau change sans que la page bouge : au clavier et au
  // lecteur d'écran, rien ne le signalerait. On y porte donc le focus à chaque
  // changement d'étape — mais jamais au premier rendu, où il n'y a rien de neuf.
  const ancrePanneau = useRef<HTMLDivElement | null>(null);
  const etapePrecedente = useRef(clePanneau);
  useEffect(() => {
    if (etapePrecedente.current === clePanneau) {
      return;
    }
    etapePrecedente.current = clePanneau;
    if (etape !== "invite") {
      ancrePanneau.current?.focus();
    }
  }, [clePanneau, etape]);

  return (
    <>
      <PageHeader
        eyebrow="Portefeuille"
        title="Achat de packs"
        description="Créditer des tokens sur le portefeuille, au prix et à la validité figés au moment de l'achat."
        actions={
          <Button
            variant="ghost"
            onClick={catalogue.reload}
            loading={catalogue.loading && catalogue.data !== null}
          >
            Recharger le catalogue
          </Button>
        }
      />

      <div className={styles.agencement}>
        <section className={styles.colonne} aria-labelledby="titre-catalogue">
          <SectionTitle id="titre-catalogue">
            Packs en vente
          </SectionTitle>

          <AsyncBoundary
            state={catalogue}
            errorTitle="Catalogue indisponible"
            skeleton={
              <div className={styles.squelettePacks} aria-busy="true">
                <Skeleton variant="block" height="14rem" />
                <Skeleton variant="block" height="14rem" />
                <Skeleton variant="block" height="14rem" />
              </div>
            }
            isEmpty={(donnees) => donnees.packs.length === 0}
            // Un catalogue vide n'est pas une panne : la grille en vigueur ne
            // propose simplement aucun pack. Le dire autrement qu'un achat
            // impossible, sinon on cherche un bogue là où il n'y en a pas.
            empty={
              <EmptyState
                icon={<IconePanier />}
                title="Aucun pack en vente"
                description="La grille en vigueur ne propose aucun pack à ce jour. Rien à acheter tant que le catalogue reste vide."
                action={
                  <Button variant="secondary" onClick={catalogue.reload}>
                    Recharger le catalogue
                  </Button>
                }
              />
            }
          >
            {(donnees) => (
              <ul className={styles.grillePacks} ref={grille}>
                {/* Les packs arrivent triés par prix croissant du serveur : les
                    réordonner ici ferait diverger l'écran du contrat. */}
                {donnees.packs.map((pack, rang) => (
                  <AchatPackCarte
                    key={pack.id}
                    pack={pack}
                    choisi={packChoisi?.id === pack.id}
                    onChoisir={choisirPack}
                    envoiEnCours={envoiEnCours}
                    gamme={
                      gamme.get(pack.id) ?? {
                        rang,
                        total: donnees.packs.length,
                        prixUnitaireCents: 0n,
                      }
                    }
                  />
                ))}
              </ul>
            )}
          </AsyncBoundary>
        </section>

        <aside
          className={cx(styles.colonne, styles.panneau)}
          aria-labelledby="titre-panneau"
          ref={entreePanneau}
        >
          <SectionTitle id="titre-panneau">
            {etape === "recu" ? "Reçu" : "Confirmation"}
          </SectionTitle>

          {/* `tabIndex={-1}` : la zone n'entre pas dans l'ordre de tabulation,
              mais peut recevoir le focus par programme au changement d'étape. */}
          <div className={styles.panneauFocus} tabIndex={-1} ref={ancrePanneau}>
            {etape === "recu" && recu !== null && (
              <AchatRecu
                recu={recu}
                onRacheter={() => {
                  // Second exemplaire = second fait : clé neuve, sinon le
                  // serveur rejouerait le reçu déjà émis sans rien créditer.
                  reinitialiserAchat();
                  renouvelerCle();
                }}
              />
            )}

            {etape === "confirmation" && packChoisi !== null && (
              // La secousse se pose sur une enveloppe et non sur la `Card` :
              // `Card` n'expose pas de `ref`, et lui en ajouter une serait
              // toucher à une primitive partagée pour le besoin d'un seul écran.
              <div ref={refSecousse}>
                <Card
                  data-anim="card"
                  title="Confirmer l'achat"
                  subtitle={packChoisi.label}
                  footer={
                    <p className={styles.mentionIdempotence}>
                      Envoi protégé par la clé{" "}
                      <span className={styles.cleIdempotence}>{cleIdempotence}</span>. Elle
                      reste la même à chaque tentative : un double envoi ne crédite
                      qu'une fois.
                    </p>
                  }
                >
                  <dl className={styles.recapitulatif}>
                    <dt>Prix</dt>
                    <dd className="lde-tabular">{formatEuros(packChoisi.price_cents)}</dd>
                    <dt>Tokens crédités</dt>
                    <dd className="lde-tabular">{formatTokens(packChoisi.tokens)}</dd>
                    <dt>Validité</dt>
                    <dd>
                      {packChoisi.validity_days}{" "}
                      {packChoisi.validity_days > 1 ? "jours" : "jour"}
                    </dd>
                  </dl>

                  <p className={styles.mentionIdempotence}>
                    {esn
                      ? "Achat enregistré sans prélèvement : il est réglé hors plateforme (virement)."
                      : compte.data?.payment_method_label != null
                        ? `Prélevé sur ${compte.data.payment_method_label}.`
                        : null}
                  </p>

                  {blocage !== null && (
                    <Alert tone="warning" role="status" title={blocage}>
                      <Link to={ROUTES.parametresPaiement}>Paramètres → Paiement</Link>. Pour un
                      règlement par virement, demandez à votre ESN d'enregistrer l'achat.
                    </Alert>
                  )}

                  {erreurAchat !== null && <AchatRefus erreur={erreurAchat} />}

                  <div className={styles.actionsPanneau}>
                    <Button
                      variant="primary"
                      size="lg"
                      fullWidth
                      loading={envoiEnCours}
                      disabled={blocage !== null || (!esn && compte.data === null)}
                      onClick={() => void confirmerAchat()}
                    >
                      {erreurAchat !== null ? "Réessayer l'achat" : esn ? "Enregistrer l'achat" : "Confirmer et prélever"}
                    </Button>
                  </div>
                </Card>
              </div>
            )}

            {etape === "invite" && (
              <Card data-anim="card">
                <EmptyState
                  compact
                  icon={<IconePanier />}
                  title="Aucun pack retenu"
                  description="Choisissez un pack dans la liste : son récapitulatif et la confirmation d'achat s'afficheront ici."
                />
              </Card>
            )}
          </div>
        </aside>
      </div>
    </>
  );
}

/**
 * Pourquoi l'entreprise ne peut pas confirmer, ou `null`. Le compte encore en
 * lecture ne bloque pas l'affichage : le bouton reste seulement inactif.
 */
function raisonBlocage(compte: BillingAccount | null): string | null {
  if (compte === null) {
    return null;
  }
  if (compte.suspended_at !== null) {
    return "Un prélèvement est resté impayé : les achats sont suspendus jusqu'à son règlement.";
  }
  if (compte.payment_method !== "active") {
    return "Un achat se règle par prélèvement : signez d'abord un mandat SEPA ou enregistrez une carte.";
  }
  return null;
}
